// External deps
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, ContentCard, Input, ModalGeneric, Switch } from 'ypf-pas-general-ui-components';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';
import { useFormikContext } from 'formik';
import { Row } from 'react-bootstrap';

// Internal deps
import AccountAssignmentObject from '../../AccountAssignmentObject/AccountAssignmentObject';
import {
  eventPendingAssignmentId,
  eventPendingAssignmentName,
  ubicationTypeEnum
} from '../../../utils/dataStatus';
import { stepEnum, initialServiceAccordion } from '../Constants';
import AddUbicationForm from '../../AddUbicationForm/AddUbicationForm';
import { getUbicationLabel } from '../../../utils/getUbicationLabel';
import ModalPublicSwitchTemplate from '../../Templates/RequestTemplate/ModalPublicSwitchTemplate/ModalPublicSwitchTemplate';
import useRequestDetailPermissions from '../../../hooks/useRequestDetailPermissions';

import useUbicationForm from './useUbicationForm';

import './UbicationForm.scss';

const isUbicationInvalid = (values) =>
  !values.ubication?.ubicationType ||
  !values.ubication?.ubication ||
  !values.ubication?.uunn ||
  (values.ubication?.ubicationType?.id === ubicationTypeEnum.pozo
    ? !values.ubication?.team || !values.ubication?.event
    : false);

const isTemplateInvalid = (values) =>
  !values.templateName?.length || !values.ubication.ubicationType || !values.ubication.uunn;

const shouldDisableContinue = (values, pendingEventWasSelected, errors) => {
  if (!values?.isTemplate) {
    return (
      (!pendingEventWasSelected &&
        errors?.imputationObject?.wells &&
        errors?.imputationObject?.wells?.length > 0) ||
      isUbicationInvalid(values)
    );
  }

  return isTemplateInvalid(values);
};

const hasErrorAndTouched = (errors, touched, field) =>
  errors?.ubication?.[field] && touched?.ubication?.[field]
    ? !!touched?.ubication?.[field] && !!errors?.ubication?.[field]
    : false;

const UbicationForm = ({
  id,
  setShowNextStep,
  isAutocomplete,
  isApplyTemplateRequest,
  autocompletWells,
  refreshWells,
  fatherStep,
  setFatherStep,
  isDraft,
  setIsApplyTemplateRequest,
  disabledAllFields,
  createSDS
}) => {
  const [showModalEditPublicTemplate, setShowModalEditPublicTemplate] = useState(false);
  const { values, setFieldValue, setFieldTouched, touched, errors } = useFormikContext();
  const {
    permissions: { typeAndObjectImputation }
  } = useRequestDetailPermissions({ notifyTo: 3, isASDS: true, isOyM: false });

  const parameters = [
    isAutocomplete,
    autocompletWells,
    refreshWells,
    isDraft,
    false,
    createSDS,
    values?.ubication?.ubicationType?.id === ubicationTypeEnum.locacion
  ];

  const {
    aditionalTeams,
    cantWells,
    containerRef,
    equipment,
    event,
    fetchDataUbication,
    getAccordionFieldName,
    getEventIdPozo,
    getEventsIdTeam,
    getOperationTypes,
    getUnitsBusiness,
    getUnitsBusinessId,
    getUnitsBusinessOfUser,
    handleSwitchAdditionTeams,
    operationTypes,
    optionUnits,
    resetField,
    setEventState,
    setShowModal,
    setStep,
    setTeamState,
    setUbicationState,
    setUunnState,
    showModal,
    getOperationByInstallationId,
    fetchDataInstallation,
    fetchDataClassification,
    getOperationByClassificationId,
    getClassificationByUbicationId,
    step,
    ubicationTypeSelect,
    getTypeOfEventsOptions,
    typeOfEventOptions
  } = useUbicationForm(...parameters);

  const pendingEventWasSelected =
    values.ubication?.event?.id === eventPendingAssignmentId ||
    values?.imputationObject?.wells[0]?.event?.id === eventPendingAssignmentId ||
    values?.ubication?.event?.nombre === eventPendingAssignmentName;

  useEffect(() => {
    const isRequestTemplate = window.location.pathname.includes('request');

    if (isRequestTemplate) {
      setFieldValue('isTemplate', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetAllServices = () => {
    setFieldValue(`accordions`, [initialServiceAccordion]);
  };

  const resetWellForm = () => {
    const { imputationObject } = values;

    resetField(imputationObject, 'imputationObject', {
      wells: [],
      wellsCopy: []
    });
  };

  const ubicationLabel = getUbicationLabel(values?.ubication?.ubicationType?.id);

  // Lin 83 - Rollback Plantillas solicitud: Filtro las opciones para solo mostrar servicios de torre
  const location = useLocation();
  const ubicationTypeOptions = ubicationTypeSelect?.filter(
    (ubication) =>
      ubication.id === ubicationTypeEnum.pozo || ubication.id === ubicationTypeEnum.locacion
  );

  // Lin 89 - Borrar de 83 a 89 y cambiar options por ubicationTypeSelect cuando se quite el Rollback
  const objectUbicationType = {
    label: 'Ubicación del servicio',
    placeholder: 'Buscar por tipo de ubicación',
    isDisabled: disabledAllFields,
    options:
      location.pathname === '/generate-template/request' ||
      location.pathname.includes('edit-template/request')
        ? ubicationTypeOptions
        : ubicationTypeSelect,
    name: 'ubication.ubicationType',
    onChange: (val) => {
      const isOyM = val?.id !== ubicationTypeEnum.locacion && val?.id !== ubicationTypeEnum.pozo;

      setFieldValue('changeDependencieApprover', values.changeDependencieApprover + 1);
      setFieldValue('isOyM', isOyM);
      if (val?.id !== values?.ubication?.ubicationType?.id) {
        setFieldValue('ubication.ubicationType', val);
        resetField(values?.ubication?.uunn, 'ubication.uunn', null);
        resetField(values?.ubication?.ubication, 'ubication.ubication', '');
        resetField(values?.ubication?.event, 'ubication.event', null);
        resetField(values?.ubication?.team, 'ubication.team', null);
        resetField(values?.ubication?.installation, 'ubication.installation', null);
        resetField(values?.ubication?.classification, 'ubication.classification', null);
        resetAllServices();
        resetWellForm();
        setIsApplyTemplateRequest(false);
        if (val?.id) {
          getUnitsBusiness(val?.id);
        }
      }
      setShowNextStep(false);
      setStep(stepEnum.ubication);
      setFatherStep(stepEnum.ubication);
    },
    value: values?.ubication?.ubicationType
  };

  const handleUunnChange = (val) => {
    if (val?.id !== values?.ubication?.uunn?.id) {
      setFieldValue('ubication.uunn', val);
      resetField(values?.ubication?.event, 'ubication.event', null);
      resetField(values?.ubication?.team, 'ubication.team', null);
      resetField(values?.ubication?.ubication, 'ubication.ubication', null);
      resetField(values?.ubication?.installation, 'ubication.installation', null);
      resetAllServices();
      setIsApplyTemplateRequest(false);

      values.accordions?.forEach((accordion, accordionIndex) => {
        accordion.provider?.providers?.forEach((_, index) => {
          resetField(
            accordion?.provider?.providers[index]?.notificate,
            getAccordionFieldName(accordionIndex, `provider.providers[${index}].notificate`),
            null
          );
        });
      });
      setUunnState(val);
    }
    setShowNextStep(false);
    setStep(stepEnum.ubication);
    setFatherStep(stepEnum.ubication);
  };

  const objectUunn = {
    options: Array.isArray(optionUnits) ? optionUnits : [],
    onChange: (val) => handleUunnChange(val),
    label: 'Unidad de Negocio',
    placeholder: 'Buscar por unidad de Negocio',
    isDisabled: !values.ubication?.ubicationType || disabledAllFields,
    errors: errors.ubication?.uunn,
    value: values.ubication?.uunn,
    name: 'ubication.uunn'
  };

  const handleUbicationChange = (val) => {
    if (val?.id !== values?.ubication?.ubication?.id) {
      setFieldValue('ubication.ubication', val);
      setFieldTouched('ubication.ubication');
      resetField(values?.ubication?.event, 'ubication.event', null);
      resetField(values?.ubication?.team, 'ubication.team', null);
      resetField(values?.ubication?.typeEvent, 'ubication.typeEvent', null);
      if (!val && values.ubication.installation?.id) {
        resetField(values?.ubication?.installation, 'ubication.installation', null);
      }
      setFieldValue('imputationObject', {
        wells: [],
        wellsCopy: []
      });
      if (
        values.ubication?.ubicationType?.id === ubicationTypeEnum.genericaOverhead ||
        values.ubication?.ubicationType?.id === ubicationTypeEnum.genericaProyecto
      ) {
        getClassificationByUbicationId(val?.id);
      }
      if (val) {
        getUnitsBusinessId(val?.id);
      } else if (!isApplyTemplateRequest) {
        setFieldValue('ubication.uunn', null);
        setFieldValue('ubication.classification', null);
        getUnitsBusinessOfUser();
      }
      // Solo si es pozo se llama al servicio de eventos
      if (val && values.ubication?.ubicationType?.id === ubicationTypeEnum.pozo) {
        getEventIdPozo(val);
      }
      if (val && values.ubication?.ubicationType?.id === ubicationTypeEnum.locacion) {
        getTypeOfEventsOptions(val?.id);
      }
      setUbicationState(val);
      if (!isApplyTemplateRequest) resetAllServices();
    }
    if (!val) {
      setFieldValue('ubication.team', null);
      setFieldValue('ubication.event', null);
      resetField(values?.ubication?.typeEvent, 'ubication.typeEvent', null);
    }
    if (!isApplyTemplateRequest) {
      setShowNextStep(false);
      setStep(stepEnum.ubication);
    }

    return setFatherStep(stepEnum.ubication);
  };

  const objectUbication = {
    placeholder: 'Buscar por nombre',
    name: 'ubication.ubication',
    onChange: (val) => handleUbicationChange(val),
    value: values.ubication.ubication,
    isDisabled: !values.ubication?.ubicationType || disabledAllFields,
    label: ubicationLabel,
    fetchData: (value) =>
      fetchDataUbication(value, values.ubication.uunn ? values.ubication.uunn.id : 0)
  };

  const objectTypeEvent = {
    label: 'Tipo de Evento',
    placeholder: 'Selecciona tipo de evento',
    options: typeOfEventOptions,
    name: 'ubication.typeEvent',
    isDisabled: !values.ubication?.ubication,
    onChange: (val) => {
      if (val?.id !== values?.ubication?.typeEvent?.id) {
        setFieldValue('ubication.typeEvent', val);
        setFieldTouched('ubication.typeEvent');
      }
    },
    isInvalid: hasErrorAndTouched(errors, touched, 'typeEvent'),

    value: values.ubication?.typeEvent,
    errors: errors?.ubication?.typeEvent
  };

  const objectEvent = {
    label: 'Evento',
    placeholder: 'Selecciona un evento',
    options: event,
    name: 'ubication.event',
    isDisabled: !values.ubication.ubicationType || !values.ubication.ubication || disabledAllFields,
    onChange: (val) => {
      if (val?.eventoPozoId !== values?.ubication?.event?.eventoPozoId) {
        setFieldValue('ubication.event', val);
        resetField(values?.ubication?.team, 'ubication.team', null);
        setFieldValue('imputationObject.wells', []);
        if (!isApplyTemplateRequest) {
          resetAllServices();
          values?.accordions?.forEach((accordion, accordionIndex) => {
            accordion.provider?.providers?.forEach((_, index) => {
              resetField(
                accordion?.provider?.providers[index]?.contract,
                getAccordionFieldName(accordionIndex, `provider.providers[${index}].contract`),
                null
              );
            });
            accordion.provider?.providers?.forEach((_, index) => {
              resetField(
                accordion?.provider?.providers[index]?.notificate,
                getAccordionFieldName(accordionIndex, `provider.providers[${index}].notificate`),
                null
              );
            });
          });
        }
        if (val?.id !== eventPendingAssignmentId) {
          getEventsIdTeam(val);
          setEventState(val);
        }
        if (val?.id === eventPendingAssignmentId) {
          handleSwitchAdditionTeams();
        }

        getOperationTypes();
        resetField(values?.ubication?.operationType, 'ubication.operationType', null);
      }
      if (!isApplyTemplateRequest) {
        setShowNextStep(false);
        setStep(stepEnum.ubication);
        setFatherStep(stepEnum.ubication);
      }
    },
    isInvalid:
      errors?.ubication?.event && touched?.ubication?.event
        ? !!touched?.ubication?.event && !!errors?.ubication?.event
        : false,

    value: values.ubication?.event,
    errors: errors?.ubication?.event
  };

  const typeOperationObject = {
    label: 'Tipo de operación',
    placeholder: 'Tipo de operacion',
    options: operationTypes,
    name: 'ubication.operationType',
    clearable: true,
    isDisabled: !values.ubication.event || !values.ubication.ubication,
    onChange: (val) => {
      if (!val) {
        setFieldValue('ubication.operationType', null);
        resetField(values?.ubication?.team, 'ubication.team', null);
        setFieldValue('imputationObject.wells', []);
        resetAllServices();
      }
      if (val?.eventoPozoId !== values?.ubication?.event?.eventoPozoId) {
        setFieldValue('ubication.operationType', val);
        resetField(values?.ubication?.team, 'ubication.team', null);
        setFieldValue('imputationObject.wells', []);
        resetAllServices();
        values?.accordions?.forEach((accordion, accordionIndex) => {
          accordion.provider?.providers?.forEach((_, index) => {
            resetField(
              accordion?.provider?.providers[index]?.contract,
              getAccordionFieldName(accordionIndex, `provider.providers[${index}].contract`),
              null
            );
          });
          accordion.provider?.providers?.forEach((_, index) => {
            resetField(
              accordion?.provider?.providers[index]?.notificate,
              getAccordionFieldName(accordionIndex, `provider.providers[${index}].notificate`),
              null
            );
          });
        });
      }
      if (values?.ubication?.event?.eventoPozoId === eventPendingAssignmentId) {
        getEventsIdTeam({ eventoPozoId: 0 });
      } else {
        getEventsIdTeam(val);
      }
      setShowNextStep(false);
      setStep(stepEnum.ubication);
      setFatherStep(stepEnum.ubication);
    },
    isInvalid:
      errors?.ubication?.operationType && touched?.ubication?.operationType
        ? !!touched?.ubication?.operationType && !!errors?.ubication?.operationType
        : false,

    value: values.ubication?.operationType,
    errors: errors?.ubication?.operationType
  };

  const objectTeam = {
    label: 'Equipo',
    placeholder: 'Selecciona un equipo',
    options: equipment,
    name: 'ubication.team',
    clearable: true,
    isDisabled: !values.ubication.ubicationType || !values.ubication.event || disabledAllFields,
    onChange: (val) => {
      if (val?.id !== values?.ubication?.team?.id) {
        setFieldValue('ubication.team', val);
        setFieldValue('changeDependencieApprover', values.changeDependencieApprover + 1);
        if (!isApplyTemplateRequest) {
          values?.accordions?.forEach((accordion, accordionIndex) => {
            resetField(
              accordion?.associatedUsers?.approvers.length > 0,
              getAccordionFieldName(accordionIndex, 'associatedUsers.approvers'),
              []
            );
          });
        }
        setTeamState(val);
      }
      if (!isApplyTemplateRequest) {
        setShowNextStep(false);
        setStep(stepEnum.ubication);
        setFatherStep(stepEnum.ubication);
      }
    },
    value: values.ubication.team,
    isInvalid:
      errors?.ubication?.team && touched?.ubication?.team
        ? !!touched?.ubication?.team && !!errors?.ubication?.team
        : false,
    errors: errors?.ubication?.team
  };

  const objectInstallation = {
    label: 'Instalación',
    name: 'ubication.installation',
    isDisabled: disabledAllFields,
    onChange: (value) => {
      if (value !== values.ubication.installation?.id) {
        setFieldValue('ubication.installation', value);
        setFieldTouched('ubication.installation');
        if (value) {
          getOperationByInstallationId(value.id);
        } else {
          setFieldValue('ubication.uunn', null);
          setFieldValue('ubication.ubication', null);
          resetAllServices();
          getUnitsBusinessOfUser();
        }
        setShowNextStep(false);
        setStep(stepEnum.ubication);
        setFatherStep(stepEnum.ubication);
      }
    },
    placeholder: 'Seleccione una instalación',
    value: values.ubication.installation,
    fetchData: (value) => fetchDataInstallation(value)
  };

  const objectClassification = {
    isDisabled: disabledAllFields,
    label: 'Clasificación',
    name: 'ubication.classification',
    onChange: (value) => {
      if (value?.id !== values.ubication.classification?.id) {
        setFieldValue('ubication.classification', value);
        setFieldTouched('ubication.classification');
        if (value) {
          getOperationByClassificationId(value.id);
        }
        setShowNextStep(false);
        setStep(stepEnum.ubication);
        setFatherStep(stepEnum.ubication);
      }
      if (!value) {
        setFieldValue('ubication.ubication', null);
      }
    },
    placeholder: 'Buscar por clasificación',
    value: values.ubication.classification,
    fetchData: (value) => fetchDataClassification(value)
  };

  const onClickTypeInputation = () => {
    if (!values.isTemplate && !pendingEventWasSelected) {
      setShowModal(true);
    }
  };

  const disableContinue = shouldDisableContinue(values, pendingEventWasSelected, errors);

  return (
    <div ref={containerRef}>
      <ContentCard
        mark={step === stepEnum.ubication ? 'primary' : 'none'}
        orientation="horizontal-vertical"
      >
        <>
          {values.isTemplate && (
            <>
              <Row className="d-flex align-items-center">
                <div className="d-flex align-items-center col-sm-10">
                  <Input
                    className="template-name-input w-100"
                    disabled={disabledAllFields}
                    label="Nombre de la plantilla"
                    onChange={(e) => setFieldValue('templateName', e.target.value)}
                    placeholder="Ingrese el nombre de la plantilla"
                    value={values.templateName}
                  />
                </div>
                <div className="d-flex flex-column align-items-start col-sm-2">
                  <span>Plantilla pública</span>
                  <Switch
                    checked={values.templateIsPublic}
                    disabled={disabledAllFields}
                    falseLabel="NO"
                    name="businessUnitSwitch"
                    onChange={() => {
                      if (id) setShowModalEditPublicTemplate(true);
                      setFieldValue('templateIsPublic', !values.templateIsPublic);
                    }}
                    size="sm"
                    trueLabel="SÍ"
                  />
                </div>
              </Row>
              <hr />
            </>
          )}
          <Row>
            <AddUbicationForm
              aditionalTeams={aditionalTeams}
              handleSwitchAdditionTeams={handleSwitchAdditionTeams}
              isCreateRequestService
              objectClassification={objectClassification}
              objectEvent={objectEvent}
              objectInstallation={objectInstallation}
              objectTeam={objectTeam}
              objectTypeEvent={objectTypeEvent}
              objectUbication={objectUbication}
              objectUbicationType={objectUbicationType}
              objectUunn={objectUunn}
              onClickTypeInputation={onClickTypeInputation}
              typeOperationObject={typeOperationObject}
            />
          </Row>
        </>
        {fatherStep === stepEnum.ubication && !isApplyTemplateRequest && (
          <>
            <hr />
            <div className="btn-container row justify-content-end">
              <Button
                className="button-continuar"
                disabled={disableContinue}
                label="Continuar"
                onClick={() => {
                  setShowNextStep(true);
                  setStep(stepEnum.services);
                  setFatherStep(stepEnum.services);
                }}
                type="button"
                variant="primary"
              />
            </div>
          </>
        )}
      </ContentCard>
      {showModal && (
        <ModalGeneric
          backdrop="static"
          infoModal
          onHide={() => setShowModal(false)}
          primaryAction={() => {
            setShowModal(false);
            // Fijamos wellsCopy con el form aceptado
            setFieldValue('imputationObject.wellsCopy', values.imputationObject?.wells);

            if (
              !values.isTemplate &&
              values.imputationObject.wellsCopy !== values.imputationObject?.wells
            ) {
              setStep(stepEnum.ubication);
              setFatherStep(stepEnum.ubication);
            }
          }}
          primaryActionDisabled={
            ((errors?.imputationObject?.wells?.length &&
              !values?.values?.imputationObject?.wells[0]?.event?.id ===
                eventPendingAssignmentId) ||
              values.imputationObject?.wells?.some((w) => w.add)) &&
            !values.isOyM
          }
          primaryActionLabel="Aceptar"
          secondaryAction={() => {
            setShowModal(false);
            // Restauramos wells con el backup realizado
            setFieldValue('imputationObject.wells', values.imputationObject?.wellsCopy);
          }}
          secondaryActionDisabled={values.imputationObject?.wells?.some((w) => w?.add)}
          secondaryActionLabel="Salir"
          show={showModal}
          size="xl"
          title="Detalles del tipo y objeto de imputación"
        >
          <AccountAssignmentObject
            createSDS
            disabledImputationForm={pendingEventWasSelected}
            eventId={values?.ubication?.event?.id || values?.operationType?.id}
            imputationObjectDefault={!typeAndObjectImputation?.editObjectImputation}
            locationId={values.ubication.ubication?.id}
            logicDeleted={false}
            maxCantWells={cantWells || 0}
            showDetailWell
            typeOfEventId={values?.ubication?.typeEvent?.id}
            ubicationType={values.ubication.ubicationType}
          />
        </ModalGeneric>
      )}
      {showModalEditPublicTemplate && (
        <ModalPublicSwitchTemplate
          id={id}
          setShowModalEditPublicTemplate={setShowModalEditPublicTemplate}
          showModalEditPublicTemplate={showModalEditPublicTemplate}
          toPublic={values.templateIsPublic}
        />
      )}
    </div>
  );
};

UbicationForm.propTypes = {
  isAutocomplete: PropTypes.bool,
  isApplyTemplateRequest: PropTypes.bool,
  setShowNextStep: PropTypes.func.isRequired,
  setIsApplyTemplateRequest: PropTypes.func,
  autocompletWells: PropTypes.bool,
  refreshWells: PropTypes.bool,
  setFatherStep: PropTypes.func.isRequired,
  fatherStep: PropTypes.number.isRequired,
  isDraft: PropTypes.bool,
  id: PropTypes.string,
  disabledAllFields: PropTypes.bool,
  createSDS: PropTypes.bool
};

UbicationForm.defaultProps = {
  id: null,
  isAutocomplete: false,
  isApplyTemplateRequest: false,
  autocompletWells: false,
  refreshWells: false,
  isDraft: false,
  setIsApplyTemplateRequest: () => {},
  disabledAllFields: false,
  createSDS: false
};

export default UbicationForm;
